import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { ErrorReportContext } from './error-report-context';
import { fingerprintErrorContext } from './error-report-dedup';
import { readReportFile } from './error-report-operation';

export type ReportSubmissionState = 'sending' | 'accepted' | 'uncertain';
interface Submission { reportId: string; state: ReportSubmissionState; }
const STORAGE_KEY = 'imagise.error-reports.submissions';
const PREVIOUS_STORAGE_KEY = 'imagise.support.submissions.v2';

@Injectable({ providedIn: 'root' })
export class ErrorReportStore {
  readonly #browser = isPlatformBrowser(inject(PLATFORM_ID));
  readonly #incident = signal<ErrorReportContext | null>(null);
  readonly #ids = new WeakMap<ErrorReportContext, string>();
  readonly #keys = new WeakMap<ErrorReportContext, string>();
  readonly #pending = new WeakSet<ErrorReportContext>();
  #submissions: Record<string, Submission> = this.#read();
  readonly incident = this.#incident.asReadonly();

  getReportId(context: ErrorReportContext): string {
    let id = this.#ids.get(context);
    if (!id) {
      id = crypto.randomUUID();
      this.#ids.set(context, id);
    }
    return id;
  }

  async beginSubmission(context: ErrorReportContext): Promise<boolean> {
    if (this.#pending.has(context)) return false;
    this.#pending.add(context);
    try {
      const key = this.#keys.get(context) ?? await this.#fingerprint(context);
      this.#keys.set(context, key);
      const existing = this.#submissions[key];
      if (existing) {
        this.#ids.set(context, existing.reportId);
        return false;
      }
      this.#submissions[key] = { reportId: this.getReportId(context), state: 'sending' };
      this.#save();
      return true;
    } finally {
      this.#pending.delete(context);
    }
  }

  submissionState(context: ErrorReportContext): ReportSubmissionState | undefined {
    const key = this.#keys.get(context);
    return key ? this.#submissions[key]?.state : undefined;
  }

  markAccepted(context: ErrorReportContext): void { this.#setState(context, 'accepted'); }
  markUncertain(context: ErrorReportContext): void { this.#setState(context, 'uncertain'); }

  allowRetry(context: ErrorReportContext): void {
    const key = this.#keys.get(context);
    if (key) delete this.#submissions[key];
    this.#save();
  }

  capture(context: ErrorReportContext): void { this.#incident.set(context); }
  clear(): void { this.#incident.set(null); }

  #setState(context: ErrorReportContext, state: ReportSubmissionState): void {
    const key = this.#keys.get(context);
    if (key && this.#submissions[key]) this.#submissions[key].state = state;
    this.#save();
  }

  async #fingerprint(context: ErrorReportContext): Promise<string> {
    if (context.kind === 'feedback') return `feedback:${this.getReportId(context)}`;
    const operation = context.operation;
    const files = await Promise.all((operation?.files ?? []).map(async file => {
      const chunks: string[] = [];
      // Hash in bounded chunks: removing an oversized attachment must not allocate its full bytes again.
      for (let offset = 0; offset < file.blob.size; offset += 1024 * 1024) {
        const bytes = await readReportFile(file.blob.slice(offset, offset + 1024 * 1024));
        const hash = await crypto.subtle.digest('SHA-256', bytes);
        chunks.push(Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join(''));
      }
      return `${file.blob.size}:${chunks.join('')}`;
    }));
    const parameters = Object.entries(operation?.parameters ?? {}).sort(([left], [right]) => left.localeCompare(right));
    const identity = JSON.stringify([context.appName, context.appVersion, fingerprintErrorContext(context),
      operation?.operation, operation?.endpoint, operation?.method, operation?.status, parameters, files]);
    // Store a fingerprint, never file bytes, full diagnostics or user text in session history.
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity));
    return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
  }

  #read(): Record<string, Submission> {
    if (!this.#browser) return {};
    try {
      const data: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(PREVIOUS_STORAGE_KEY) ?? '{}');
      if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
      const submissions = Object.fromEntries(Object.entries(data).filter(([, value]) => value && typeof value === 'object' &&
        typeof value.reportId === 'string' && /^[0-9a-f-]{36}$/i.test(value.reportId) &&
        ['sending', 'accepted', 'uncertain'].includes(value.state)).map(([key, value]) => [key, {
          reportId: value.reportId, state: value.state === 'sending' ? 'uncertain' : value.state,
        }]));
      // Preserve accepted and uncertain sends across the endpoint migration.
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(submissions));
        sessionStorage.removeItem(PREVIOUS_STORAGE_KEY);
      } catch { /* Keep the ledger in memory when session storage is unavailable. */ }
      return submissions;
    } catch { return {}; }
  }

  #save(): void {
    if (!this.#browser) return;
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.#submissions)); } catch { /* Session fallback stays in memory. */ }
  }
}
